import { Box, Button, DatePicker, FormField, Input } from '@cloudscape-design/components';
import { FC, useState } from 'react';
import { Agp } from '../api/agp';
import { updateBatch } from '../api/batches';
import { useAuthContext } from '../contexts/AuthContext';
import { calculateExciseDuty, exciseDutyInfoNL, ProductCategory } from '../data/exciseInfo';
import { Batch } from '../models/Batch';
import { getAgpState } from '../utils/agp';
import { capitalizeFirstLetter } from '../utils/string';
import { toDate } from '../utils/timestamp';
import { Column, Row } from './Flex';

interface IExciseDutyModalProps {
  batch: Batch | null;
  batchAgp: Agp[];
  isSparkling?: boolean;
  abvFinal: number;
  onClose: () => void;
}

const ExciseDutyModal: FC<IExciseDutyModalProps> = ({ batch, batchAgp, abvFinal, isSparkling, onClose }) => {
  const { tenant, currency } = useAuthContext();

  const [exciseDutyAmount, setExciseDutyAmount] = useState<number>(batch?.exciseDuty?.exciseDutyAmount ?? 0);
  const [exciseDutyPaid, setExciseDutyPaid] = useState<string | undefined>(
    batch?.exciseDuty?.exciseDutyPaid ? toDate(batch.exciseDuty.exciseDutyPaid).toISOString().split('T')[0] : undefined,
  );

  const [exciseDutyPaymentReferenceId, setExciseDutyPaymentReferenceId] = useState<string>(
    batch?.exciseDuty?.exciseDutyPaymentReferenceId ?? '',
  );

  // Inside your component:
  const releasedAgps = batchAgp.filter((agp) => getAgpState(agp) !== 'unreleased');

  const totalVolumeL = releasedAgps.reduce((sum, agp) => sum + (agp.size ?? 0) * (agp.amount ?? 0), 0);

  const isSession = abvFinal <= 6;

  const category: ProductCategory = isSession ? 'session' : isSparkling ? 'sparkling' : 'still';

  const categoryInfo = exciseDutyInfoNL[category];

  const calculatedDuty = calculateExciseDuty(totalVolumeL, abvFinal, isSparkling ?? false);

  const handleSave = async () => {
    if (!batch) return;

    // Save the excise duty information
    const batchToSave: Partial<Batch> = {
      exciseDuty: {
        exciseDutyAmount,
        exciseDutyPaid: exciseDutyPaid ? new Date(exciseDutyPaid) : new Date(),
        exciseDutyPaymentReferenceId: exciseDutyPaymentReferenceId ?? '',
      },
    };
    await updateBatch(batch.id, batchToSave, tenant);
    onClose();
  };

  return (
    <Column>
      <Box variant="awsui-key-label">{`Please enter information about the excise duty for batch ${batch?.batchId}`}</Box>
      <FormField
        label="Estimated excise duty (based on bottles in AGP and ABV)"
        description={`Category: ${capitalizeFirstLetter(category)} (${abvFinal.toFixed(2)}% ABV)`}
      >
        <Box variant="p">
          {`${totalVolumeL.toFixed(2)}L × ${currency(categoryInfo.ratePer100L.toFixed(2))} per 100L = `}
          <b>{currency(calculatedDuty.toFixed(2))}</b>
        </Box>
        <Box variant="small">
          <i>{categoryInfo.description}</i>
        </Box>
      </FormField>
      <FormField label="Amount paid" description="Please enter the amount paid for excise duty">
        <Input
          type="number"
          value={exciseDutyAmount?.toString() ?? '0'}
          onChange={({ detail }) => setExciseDutyAmount(Number(detail.value))}
        />
      </FormField>
      <FormField
        label="Date paid"
        description="Please enter the date when the excise duty was paid. Empty if not paid yet"
      >
        <DatePicker
          value={exciseDutyPaid ?? new Date().toISOString()}
          onChange={({ detail }) => setExciseDutyPaid(detail.value)}
        />
      </FormField>
      <FormField label="Payment reference ID" description="Please enter the payment reference ID">
        <Input
          value={exciseDutyPaymentReferenceId}
          onChange={({ detail }) => setExciseDutyPaymentReferenceId(detail.value)}
        />
      </FormField>
      <Row align="end">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSave}>
          Save
        </Button>
      </Row>
    </Column>
  );
};

export default ExciseDutyModal;
